import { useState, useMemo } from "react"
import {
  ChevronDownIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline"
import { Image } from "~/ui-rtk/components/ui/common/Image"
import type { AdsetStructure, SelectedCreative } from "../types"

const BUILD_YOUR_OWN_INDEX = -1
const MAX_THUMBNAILS = 8

interface StructureSelectionProps {
  structures: AdsetStructure[]
  onSelect: (structure: AdsetStructure) => void
  onBuildOwn: () => void
  onBack: () => void
  selectedCreatives?: SelectedCreative[]
}

export default function StructureSelection({
  structures,
  onSelect,
  onBuildOwn,
  onBack,
  selectedCreatives,
}: StructureSelectionProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const creativesById = useMemo(() => {
    if (!selectedCreatives) return null
    const map = new Map<string, SelectedCreative>()
    for (const c of selectedCreatives) {
      map.set(c.id, c)
    }
    return map
  }, [selectedCreatives])

  const handleCardClick = (index: number) => {
    setSelectedIndex(index)
  }

  const handleToggleExpand = (e: React.MouseEvent, index: number) => {
    e.stopPropagation()
    setExpandedIndex(expandedIndex === index ? null : index)
  }

  const isCreativeFlow = Boolean(
    selectedCreatives && selectedCreatives.length > 0,
  )

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-basic-white mb-1">
          Choose a Structure
        </h2>
        <p className="text-sm text-basic-light/60">
          {isCreativeFlow
            ? "Select how you'd like your creative grouped into adsets. Each option offers a different perspective."
            : "Select how you'd like your adsets grouped. Each option offers a different perspective on your product catalog."}
        </p>
      </div>

      <div className="space-y-3">
        {structures.map((structure, index) => {
          const isSelected = selectedIndex === index
          const isExpanded = expandedIndex === index

          return (
            <div
              key={index}
              role="button"
              tabIndex={0}
              className={`rounded-lg border transition-colors cursor-pointer ${
                isSelected
                  ? "border-primary ring-1 ring-primary bg-system-gray-900/50"
                  : "border-system-gray-700/50 bg-system-gray-900/50 hover:border-system-gray-700"
              }`}
              onClick={() => handleCardClick(index)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  handleCardClick(index)
                }
              }}
            >
              <div className="flex items-start gap-3 p-4">
                {/* Radio indicator */}
                <div className="mt-0.5 shrink-0">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      isSelected
                        ? "border-primary"
                        : "border-system-gray-700/80"
                    }`}
                  >
                    {isSelected && (
                      <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-basic-white">
                      {structure.name}
                    </span>
                    <span className="text-xs bg-system-gray-700/50 text-basic-light/60 px-2 py-0.5 rounded-full">
                      {structure.adsets.length} adsets
                    </span>
                  </div>
                  <p className="text-xs text-basic-light/50 mt-1">
                    {structure.description}
                  </p>
                </div>

                {/* Expand/collapse chevron */}
                <button
                  className="btn btn-ghost btn-sm btn-square shrink-0"
                  onClick={e => handleToggleExpand(e, index)}
                  title={isExpanded ? "Collapse" : "Expand"}
                >
                  <ChevronDownIcon
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>

              {/* Expanded adset details */}
              {isExpanded && (
                <div className="border-t border-system-gray-700/50 px-4 pb-4 pt-3 space-y-3">
                  {structure.adsets.map((adset, adsetIndex) => {
                    const thumbnails =
                      creativesById && adset.creativeIds
                        ? adset.creativeIds
                            .map(id => creativesById.get(id))
                            .filter((c): c is SelectedCreative =>
                              Boolean(c?.thumbnail),
                            )
                        : []
                    const overflowCount = Math.max(
                      0,
                      thumbnails.length - MAX_THUMBNAILS,
                    )

                    return (
                      <div
                        key={adsetIndex}
                        className="bg-system-gray-900/80 rounded-md p-3"
                      >
                        <p className="text-sm font-medium text-basic-white">
                          {adset.title}
                        </p>
                        <p className="text-xs text-basic-light/50 mt-1">
                          {adset.description}
                        </p>

                        {/* Creative thumbnails */}
                        {thumbnails.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-2">
                            {thumbnails
                              .slice(0, MAX_THUMBNAILS)
                              .map(creative => (
                                <Image
                                  key={creative.id}
                                  src={creative.thumbnail}
                                  fallback={creative.thumbnail}
                                  alt={creative.title}
                                  classes={{
                                    wrapper:
                                      "w-10 h-10 rounded overflow-hidden",
                                    image: "w-10 h-10 object-cover",
                                  }}
                                />
                              ))}
                            {overflowCount > 0 && (
                              <span className="text-xs text-basic-light/50 ml-1">
                                +{overflowCount}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Example handles pills */}
                        {adset.exampleHandles.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {adset.exampleHandles.map((handle, hi) => (
                              <span
                                key={hi}
                                className="text-xs bg-system-gray-700/50 text-basic-light/60 px-2 py-0.5 rounded-full"
                              >
                                {handle}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}

        {/* Build Your Own card */}
        <div
          role="button"
          tabIndex={0}
          className={`rounded-lg border transition-colors cursor-pointer ${
            selectedIndex === BUILD_YOUR_OWN_INDEX
              ? "border-primary ring-1 ring-primary bg-system-gray-900/50"
              : "border-system-gray-700/50 bg-system-gray-900/50 hover:border-system-gray-700"
          }`}
          onClick={() => handleCardClick(BUILD_YOUR_OWN_INDEX)}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              handleCardClick(BUILD_YOUR_OWN_INDEX)
            }
          }}
        >
          <div className="flex items-start gap-3 p-4">
            <div className="mt-0.5 shrink-0">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedIndex === BUILD_YOUR_OWN_INDEX
                    ? "border-primary"
                    : "border-system-gray-700/80"
                }`}
              >
                {selectedIndex === BUILD_YOUR_OWN_INDEX && (
                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <WrenchScrewdriverIcon className="w-4 h-4 text-basic-light/60" />
                <span className="text-sm font-semibold text-basic-white">
                  Build Your Own
                </span>
              </div>
              <p className="text-xs text-basic-light/50 mt-1">
                {isCreativeFlow
                  ? "Create a custom structure by naming your adsets and manually assigning creative to each one."
                  : "Create a custom structure by naming your adsets and assigning products to each one manually."}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <button className="btn btn-ghost" onClick={onBack}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
          Back
        </button>
        <button
          className="btn btn-primary"
          disabled={selectedIndex === null}
          onClick={() => {
            if (selectedIndex === BUILD_YOUR_OWN_INDEX) {
              onBuildOwn()
            } else if (selectedIndex !== null) {
              onSelect(structures[selectedIndex])
            }
          }}
        >
          {selectedIndex === BUILD_YOUR_OWN_INDEX
            ? "Start Building"
            : "Use This Structure"}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
